import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Heading,
  Text,
  Card,
  Flex,
  Button,
  Badge,
  IconButton,
  Tooltip,
} from '@radix-ui/themes';
import {
  ListBulletIcon,
  PlusIcon,
  Pencil1Icon,
  TrashIcon,
  LockClosedIcon,
} from '@radix-ui/react-icons';
import { GetListsDocument } from '../../graphql/queries/GetLists.generated';
import { useDeleteListMutation } from '../../graphql/mutations/DeleteList.generated';
import GraphQLTable, { Column } from '../../components/GraphQLTable/GraphQLTable';
import type { GetListsQuery } from '../../graphql/queries/GetLists.generated';

type ListNode = NonNullable<
  GetListsQuery['lists']['edges'][number]['node']
>;

const ListList: React.FC = () => {
  const navigate = useNavigate();
  const [deleteList] = useDeleteListMutation();

  const handleDelete = async (e: React.MouseEvent, listId: string) => {
    e.stopPropagation();
    if (confirm('Are you sure you want to delete this list?')) {
      await deleteList({ variables: { id: listId } });
    }
  };

  const handleEditClick = (e: React.MouseEvent, listId: string) => {
    e.stopPropagation();
    navigate(`/lists/${listId}`);
  };

  const columns: Column<ListNode>[] = [
    {
      header: 'Name',
      accessor: 'name',
      render: ({ row }) => (
        <Flex direction="column">
          <Flex align="center" gap="1">
            <Text size="2" weight="medium">
              {row.name}
            </Text>
            {row.isSystem && (
              <Tooltip content="System-managed list (read-only)">
                <Box
                  style={{
                    color: 'var(--gray-9)',
                    display: 'flex',
                  }}
                >
                  <LockClosedIcon width={12} height={12} />
                </Box>
              </Tooltip>
            )}
          </Flex>
          {row.description && (
            <Text size="1" color="gray">
              {row.description}
            </Text>
          )}
        </Flex>
      ),
    },
    {
      header: 'Type',
      accessor: 'itemType',
      render: ({ value }) => (
        <Badge color="gray" variant="soft">
          {value as string}
        </Badge>
      ),
    },
    {
      header: 'Items',
      render: ({ row }) => (
        <Badge color="blue" variant="soft">
          {row.items.totalCount.toLocaleString()}
        </Badge>
      ),
    },
    {
      header: 'Created',
      accessor: 'createdAt',
      render: ({ value }) => (
        <Text size="2" color="gray">
          {new Date(value as string).toLocaleDateString()}
        </Text>
      ),
    },
    {
      header: null,
      render: ({ row }) =>
        row.isSystem ? null : (
          <Flex gap="1" justify="end">
            <Tooltip content="Edit">
              <IconButton
                size="1"
                variant="ghost"
                color="gray"
                onClick={(e) => handleEditClick(e, row.id)}
              >
                <Pencil1Icon width={14} height={14} />
              </IconButton>
            </Tooltip>
            <Tooltip content="Delete">
              <IconButton
                size="1"
                variant="ghost"
                color="red"
                onClick={(e) => handleDelete(e, row.id)}
              >
                <TrashIcon width={14} height={14} />
              </IconButton>
            </Tooltip>
          </Flex>
        ),
      style: { width: '80px' },
    },
  ];

  return (
    <Box>
      <Box pb="4" px="1">
        <Flex align="center" justify="between">
          <Flex align="center" gap="3">
            <Box
              style={{
                padding: '8px',
                borderRadius: '8px',
                backgroundColor: 'var(--blue-3)',
                color: 'var(--blue-11)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ListBulletIcon width={24} height={24} />
            </Box>
            <Box>
              <Heading as="h1" size="5" weight="bold">
                Lists
              </Heading>
              <Text size="2" color="gray">
                Organize addresses into lists for tracking and automation
              </Text>
            </Box>
          </Flex>
          <Button onClick={() => navigate('/lists/new')}>
            <PlusIcon />
            New List
          </Button>
        </Flex>
      </Box>

      <Card size="2">
        <Box
          style={{
            marginLeft: 'calc(-1 * var(--space-4))',
            marginRight: 'calc(-1 * var(--space-4))',
            marginBottom: 'calc(-1 * var(--space-4))',
          }}
        >
          <GraphQLTable
            query={GetListsDocument}
            columns={columns}
            connectionPath="lists"
            size="2"
            removeBottomBorder
            noDataMessage="No lists yet. Create your first list to get started."
            onRowClick={(row) => navigate(`/lists/${row.id}`)}
          />
        </Box>
      </Card>
    </Box>
  );
};

export default ListList;
